/**
 * ProfileFollowedAuthors.jsx — sub-page listing authors the user follows.
 * Tap an author → /writer/{user_id}. Feb 26 2026.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users as UsersIcon, BookOpen, ChevronRight, Loader2 } from 'lucide-react';
import ProfileSubPage from '../components/ProfileSubPage';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileFollowedAuthors = () => {
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAuthors = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/me/followed-authors`);
      if (r.ok) {
        const d = await r.json();
        setAuthors(d.authors || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAuthors(); }, [fetchAuthors]);

  return (
    <ProfileSubPage title="Followed Authors" icon={UsersIcon} accent="emerald">
      {loading && (
        <div className="text-center text-slate-400 text-sm py-6">
          <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…
        </div>
      )}
      {!loading && authors.length === 0 && (
        <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-8 text-center text-sm text-slate-400" data-testid="followed-authors-empty">
          You haven't followed any authors yet. Save a book to start following them automatically.
        </div>
      )}
      {!loading && authors.map((a) => (
        <button
          key={a.user_id}
          type="button"
          onClick={() => navigate(`/writer/${a.user_id}`)}
          className="w-full text-left p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-indigo-500/50 hover:bg-slate-800 transition-colors flex items-center gap-3"
          data-testid={`followed-author-${a.user_id}`}
        >
          {a.picture ? (
            <img src={a.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-white truncate">{a.name}</div>
            {a.bio && <div className="text-xs text-slate-400 line-clamp-1">{a.bio}</div>}
            <div className="flex items-center gap-3 mt-1 text-[11px]">
              <span className="text-emerald-300 inline-flex items-center gap-1">
                <UsersIcon className="w-3 h-3" /> {a.followers} followers
              </span>
              <span className="text-amber-300 inline-flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> {a.books_published} books
              </span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
      ))}
    </ProfileSubPage>
  );
};

export default ProfileFollowedAuthors;
